import {
  SetStateAction,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import {
  getListETax,
  getBatchDetails,
  downloadETaxItemPdf,
} from "../services";
import { ETaxBatch, ETaxBatchDetail } from "../types/e-tax.type";
import { formatDate } from "../utils/date";
import { formatNumber, numberWithCommas } from "../utils";
import { AxiosError } from "axios";
import { pushGlobalError } from "../errors/globalError";
import {
  ETAX_PDF_DOWNLOAD_ERROR_MESSAGE,
  ETAX_PDF_NOT_FOUND_MESSAGE,
} from "../utils/hooks/useEtaxPdfDownload";
import ETaxPagination from "../components/ETax/Pagination";
import {
  ETaxBatchStatusCell,
  LoadErrorState,
  headCellSx,
} from "../components/ETax/documentCells";
import {
  downloadBlob,
  getFilenameFromContentDisposition,
} from "../components/SalesReportExport/utils";
import {
  buildDateRangeSearch,
  resolveDateRangeParams,
} from "../components/ETax/dateRangeParams";
import {
  clampPage,
  parseLimitParam,
  parsePageParam,
} from "../components/ETax/Pagination";
import { getEtaxBreadcrumbLabel } from "../components/ETax/etaxTitle";

const LIMIT = 10;
const COLUMN_COUNT = 9;

/**
 * Only shown when the two figures differ. A batch whose bills all fall inside the selected
 * range shows one number, so the common case never asks the reader to interpret "23 / 23".
 */
const BILL_COUNT_CAPTION = "ในช่วงเวลาที่เลือก / ทั้ง batch";

/**
 * The SALE dates the batch's counted bills came from. One send run sweeps whatever is
 * outstanding, so it routinely covers several business days — showing only the submission date
 * is what made a bill sold on the 10th look like it belonged to the 14th.
 */
/**
 * Send state of every bill the run carried.
 *
 * The success and failure counts are whole-batch figures, so the remainder is taken
 * against the whole batch too — `transaction_count` is narrowed to the selected sale-date
 * range and would leave this short, or negative, whenever part of the batch falls outside
 * it. That remainder is `in_progress` rather than "not sent": every item in a batch has
 * already been submitted.
 */
const countsOf = (batch: ETaxBatch) => ({
  success: batch.transaction_success_count,
  failed: batch.transaction_failure_count,
  in_progress: Math.max(
    batch.batch_transaction_count -
      batch.transaction_success_count -
      batch.transaction_failure_count,
    0,
  ),
});

/**
 * Whether จำนวนบิล has to spell out both figures. A newer backend sends the batch's own counted
 * total, which is the right thing to compare against: comparing with every item the run carried
 * flagged batches whose "missing" bills were simply failed or cancelled, not outside the range.
 */
const getBatchBillTotal = (batch: ETaxBatch): number =>
  batch.batch_counted_transaction_count ?? batch.batch_transaction_count;

const formatSaleDateRange = (batch: ETaxBatch): string => {
  if (!batch.sale_date_from || !batch.sale_date_to) return "-";
  const from = formatDate(batch.sale_date_from);
  const to = formatDate(batch.sale_date_to);
  return from === to ? from : `${from} - ${to}`;
};

const renderSkeletonRows = () =>
  Array.from({ length: LIMIT }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: COLUMN_COUNT }).map((__, colIndex) => (
        <TableCell key={colIndex}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));

export default function ETaxHistoryPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dateFromParam = searchParams.get("date_from");
  const dateToParam = searchParams.get("date_to");
  const { from: startDate, to: endDate } = useMemo(
    () => resolveDateRangeParams(dateFromParam, dateToParam),
    [dateFromParam, dateToParam],
  );

  const dateSearch = buildDateRangeSearch(startDate, endDate);

  // page / limit live in the URL, so returning from a batch's detail page (back button or
  // breadcrumb history) lands on the page the user left rather than page 1.
  const page = parsePageParam(searchParams.get("page"));
  const limit = parseLimitParam(searchParams.get("limit"), LIMIT);

  const writeListParams = useCallback(
    (nextPage: number, nextLimit: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // The range is written normalised, so a swapped deep link reads correctly afterwards.
          next.delete("date_from");
          next.delete("date_to");
          new URLSearchParams(dateSearch.slice(1)).forEach((value, key) =>
            next.set(key, value),
          );
          if (nextPage > 1) next.set("page", String(nextPage));
          else next.delete("page");
          if (nextLimit !== LIMIT) next.set("limit", String(nextLimit));
          else next.delete("limit");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, dateSearch],
  );

  // The pagination control changes limit and page back to back in one handler; both writes
  // read and update this ref so the second one does not undo the first from a stale render.
  const listRef = useRef({ page, limit });
  listRef.current = { page, limit };
  const setPage = useCallback(
    (value: SetStateAction<number>) => {
      const current = listRef.current;
      const nextPage = typeof value === "function" ? value(current.page) : value;
      listRef.current = { ...current, page: nextPage };
      writeListParams(listRef.current.page, listRef.current.limit);
    },
    [writeListParams],
  );
  const setLimit = useCallback(
    (value: SetStateAction<number>) => {
      const current = listRef.current;
      const nextLimit = typeof value === "function" ? value(current.limit) : value;
      listRef.current = { ...current, limit: nextLimit };
      writeListParams(listRef.current.page, listRef.current.limit);
    },
    [writeListParams],
  );

  // Read through a ref inside the fetch, so the fetch does not re-run whenever the setter's
  // identity follows the URL.
  const setPageRef = useRef(setPage);
  setPageRef.current = setPage;

  const [totalCount, setTotalCount] = useState<number>(0);
  const [batches, setBatches] = useState<ETaxBatch[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Only the newest request may write state. Changing the date range fires a request
  // per picker edit; without this guard a slower earlier response can land last and
  // repaint the table with rows from the previous filter.
  const requestSeq = useRef<number>(0);

  const fetchHistory = useCallback(async () => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;

    setLoading(true);
    setLoadError(false);
    try {
      const {
        data: {
          data: { rows, count },
        },
      } = await getListETax(page, limit, startDate, endDate, "ALL");
      if (requestSeq.current !== seq) return;
      setBatches(rows);
      setTotalCount(count);
      // A page past the end (a stale link, or rows gone since) steps back to the last page
      // that has rows instead of showing an empty table under a non-empty count.
      const clamped = clampPage(page, count, limit);
      if (clamped !== page) setPageRef.current(clamped);
    } catch {
      // The axios interceptor surfaces the error toast; show the load error rather than
      // an empty table that reads as "no batches".
      if (requestSeq.current !== seq) return;
      setBatches([]);
      setTotalCount(0);
      setLoadError(true);
    } finally {
      if (requestSeq.current === seq) setLoading(false);
    }
  }, [page, limit, startDate, endDate]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleViewDetail = (uuid: string) => {
    navigate(`/e-tax/history/${uuid}${dateSearch}`);
  };

  const handleDownloadBatch = async (uuid: string) => {
    if (downloadingId) return;
    setDownloadingId(uuid);
    try {
      const {
        data: { data },
      } = await getBatchDetails(uuid, startDate, endDate);
      const detail: ETaxBatchDetail = data;
      // Only the items this row counts (counted, inside the range) that have a generated PDF
      // (path_file) — the same set as the row's downloadable_count. An older backend sends
      // neither flag, and then every item with a file goes.
      const itemsToDownload = detail.items.filter(
        (item) =>
          item.is_counted !== false && item.in_range !== false && item.path_file,
      );
      if (itemsToDownload.length === 0) {
        pushGlobalError(ETAX_PDF_DOWNLOAD_ERROR_MESSAGE);
        return;
      }

      // Stream each PDF from the same-origin API and save it. A single failed
      // file is skipped; the error toast only fires if every file failed — and
      // uses the specific "not found" message when every failure was a 404.
      let successCount = 0;
      let onlyNotFound = true;
      for (const item of itemsToDownload) {
        try {
          const response = await downloadETaxItemPdf(item.etax_uuid);
          const blob = response.data as Blob;
          if (
            !(blob instanceof Blob) ||
            blob.size === 0 ||
            blob.type.includes("application/json")
          ) {
            onlyNotFound = false;
            continue;
          }
          const filename =
            getFilenameFromContentDisposition(
              response.headers["content-disposition"] as string | undefined,
            ) || `${item.transaction_no}.pdf`;
          downloadBlob(filename, blob);
          successCount += 1;
        } catch (error) {
          if ((error as AxiosError).response?.status !== 404) onlyNotFound = false;
        }
      }

      if (successCount === 0) {
        pushGlobalError(
          onlyNotFound
            ? ETAX_PDF_NOT_FOUND_MESSAGE
            : ETAX_PDF_DOWNLOAD_ERROR_MESSAGE,
        );
      }
    } catch {
      pushGlobalError(ETAX_PDF_DOWNLOAD_ERROR_MESSAGE);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        {/* Carry the active range back to the dashboard so its cards keep summarising
            exactly what this list shows. */}
        <Link
          to={`/e-tax${dateSearch}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {getEtaxBreadcrumbLabel()}
          </Typography>
        </Link>
        <Typography variant="body1" sx={{ mx: 1 }}>
          /
        </Typography>
        <Typography variant="body1" color="primary">
          ประวัติการส่ง
        </Typography>
      </Box>

      <Paper>
        <TableContainer sx={{ borderRadius: "12px" }}>
          <Table>
            <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
              <TableRow>
                <TableCell sx={headCellSx}>วันที่ยื่น</TableCell>
                <TableCell sx={headCellSx}>ช่วงวันขาย</TableCell>
                <TableCell align="right" sx={headCellSx}>
                  มูลค่ารวมสุทธิ (Net Total)
                </TableCell>
                <TableCell align="right" sx={headCellSx}>
                  มูลค่าก่อน VAT
                </TableCell>
                <TableCell align="right" sx={headCellSx}>
                  ภาษีมูลค่าเพิ่ม
                </TableCell>
                <TableCell align="right" sx={headCellSx}>
                  จำนวนบิล
                </TableCell>
                <TableCell sx={headCellSx}>สถานะ</TableCell>
                <TableCell align="center" sx={headCellSx}>
                  รายละเอียด
                </TableCell>
                <TableCell align="center">ดาวน์โหลด</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && renderSkeletonRows()}
              {!loading && loadError && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                    <LoadErrorState onRetry={fetchHistory} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && !loadError && batches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary" fontWeight={500}>
                      ไม่พบข้อมูล
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                batches.map((batch, index) => {
                  const netTotal = Number(batch.transaction_grand_total);
                  const beforeVat = batch.transaction_amount_before_vat;
                  const vat = batch.transaction_vat_amount;
                  const hasFiles = batch.downloadable_count > 0;
                  // Part of the batch's counted bills fall outside the selected range, so
                  // จำนวนบิล here is not the whole batch and both figures have to be spelled out.
                  const batchBillTotal = getBatchBillTotal(batch);
                  const isPartialBatch = batch.transaction_count !== batchBillTotal;
                  const isDownloading =
                    downloadingId !== null &&
                    downloadingId === batch.etax_batch_uuid;
                  return (
                    <TableRow
                      key={batch.etax_batch_uuid}
                      sx={{
                        "& td, & th": { padding: "8px" },
                        bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                      }}
                    >
                      <TableCell>{formatDate(batch.created_at)}</TableCell>
                      <TableCell>{formatSaleDateRange(batch)}</TableCell>
                      <TableCell align="right">
                        {formatNumber(netTotal)} บาท
                      </TableCell>
                      <TableCell align="right">
                        {formatNumber(beforeVat)} บาท
                      </TableCell>
                      <TableCell align="right">{formatNumber(vat)} บาท</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          <strong>{numberWithCommas(batch.transaction_count)}</strong>
                          {isPartialBatch &&
                            ` / ${numberWithCommas(batchBillTotal)}`}
                          {" ใบ"}
                        </Typography>
                        {isPartialBatch && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            noWrap
                          >
                            {BILL_COUNT_CAPTION}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <ETaxBatchStatusCell
                          status={batch.status}
                          counts={countsOf(batch)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="info"
                          sx={{ gap: 0.5, px: 1, borderRadius: 1.5 }}
                          onClick={() => handleViewDetail(batch.etax_batch_uuid)}
                        >
                          <VisibilityIcon fontSize="small" />
                          <Typography variant="subtitle2">ดูรายละเอียด</Typography>
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            hasFiles
                              ? "ดาวน์โหลด PDF"
                              : "ยังไม่มีไฟล์ให้ดาวน์โหลด"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={downloadingId !== null || !hasFiles}
                              onClick={() =>
                                handleDownloadBatch(batch.etax_batch_uuid)
                              }
                            >
                              {isDownloading ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <ETaxPagination
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          limit={limit}
          setLimit={setLimit}
        />
      </Paper>
    </Box>
  );
}
